import { listCases } from '@/backend/case'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/vue-query'
import { computed } from 'vue'

/**
 * This composable returns a Tanstack query for unread Cases, and functions to
 * update the query's cache when a Case is read locally or when we receive a
 * websocket message indicating that a Case has been read.
 */
export const useUnreadCases = ({ disabled = false }: { disabled?: boolean } = {}) => {
  const { workspaceId } = useWorkspaceContext()

  const queryKey = computed(() => ['unread-cases', workspaceId.value])

  const query = useQuery({
    queryKey,
    enabled: !disabled,
    queryFn: async () => {
      const response = await listCases({
        is_unread: true,
        workspaceId: workspaceId.value,
        limit: 20,
      })

      if (!response.ok) {
        throw new Error('Failed to fetch unread cases')
      }

      return new Set(response.data.data.map((c) => c.id))
    },
    placeholderData: keepPreviousData,
  })

  const client = useQueryClient()
  const markAsRead = async (caseId: string) => {
    const hasUnreadCases: { old: boolean; new: boolean } = {
      old: !!query.data.value && query.data.value.size > 0,
      new: false,
    }

    await client.setQueryData(queryKey.value, (old: Set<string>) => {
      const newSet = new Set(old)
      newSet.delete(caseId)
      return newSet
    })

    hasUnreadCases.new = !!query.data.value && query.data.value.size > 0
    if (hasUnreadCases.old && !hasUnreadCases.new) {
      /**
       * The API we use to list unread Cases is paginated, but we only fetch the first
       * page. This means that when the last unread case is read, there *might* be
       * more unread Cases in other pages of data. This watcher will reload unread
       * Cases when the last unread case is read.
       */
      query.refetch()
    }
  }

  return {
    ...query,
    /**
     * Called when a Case is marked as read, this will remove the Case from the
     * unread Cases set.
     */
    markAsRead,
  }
}
